import { localize } from "client_legacy/ndaw/culture";
import {
	computeContextMenuTitle,
	ContextMenu,
	TableView,
	VirtualTableViewColumnDefinition,
	useContextMenu,
	TableViewNoResultConfig
} from "client/Components";
import { DocumentName, DocumentNameKind } from "../DocumentName";
import { BookmarkModel } from "client/Api/Documents/Bookmarks/bookmarks";
import {
	MultiSelectableOptions,
	SelectableItemOptions,
	useIsTouchDevice
} from "client/Hooks";
import {
	ReactElement,
	Ref,
	forwardRef,
	useImperativeHandle,
	useMemo,
	useRef
} from "react";
import { BookmarkAction, BookmarkActionContext } from "./bookmarkActions";
import { useRowHover } from "client/Hooks/useRowHover";
import { DocumentNameWithQuickActions } from "../DocumentNameWithQuickActions";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type BookmarksTableViewHandle = {
	resetScrollPosition: () => void;
};

export type BookmarksTableViewProps = {
	isBusy: boolean;
	multiSelection: MultiSelectableOptions;
	selectionFor: (item: BookmarkModel) => SelectableItemOptions;
	items: readonly BookmarkModel[];
	onOpenItem: (bookmark: BookmarkModel) => void;
	selectionActions: BookmarkAction[];
	actionContext: BookmarkActionContext;
	hasSearch: boolean;
};

export const BookmarksTableView = forwardRef(
	(props: BookmarksTableViewProps, ref: Ref<BookmarksTableViewHandle>): ReactElement => {
		const {
			isBusy,
			multiSelection,
			selectionFor,
			items,
			onOpenItem,
			selectionActions,
			actionContext,
			hasSearch
		} = props;

		const tableRef: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);
		const [contextMenuHandler, contextMenuProps] = useContextMenu(tableRef);
		const isTouch: boolean = useIsTouchDevice();
		const [hoveredRowId, hoverHandler] = useRowHover<number>();

		useImperativeHandle(
			ref,
			(): BookmarksTableViewHandle => ({
				resetScrollPosition: () => {
					tableRef.current
						?.scrollTo(0, 0);
				}
			}),
			[]
		);

		const noResultConfig = useMemo(
			(): TableViewNoResultConfig | undefined => {
				if (isBusy) {
					return undefined;
				}

				return hasSearch
					? {
						icon: "magnifying-glass",
						title: localize("_NoSearchResults"),
						message: localize("_PleaseTrySearchingWithAnotherTerm")
					}
					: {
						icon: "folder-open",
						title: localize("_NoDocumentsToShow"),
						message: localize("_ThereAreCurrentlyNoDocumentsInThisFolder")
					};
			},
			[hasSearch, isBusy]
		);

		return (
			<>
				<TableView
					isVirtual
					hasNoBottomDelimiter
					columns={columnDefinitions}
					items={items}
					itemKey="id"
					trackingId="bookmark-grid"
					multiSelectable={multiSelection}
					ref={tableRef}
					height="fill"
					renderAfterItems={
						<>
							<TableView.LoadMoreIndicator
								isLoadingData={isBusy}
								hasMoreItems={false}
								onRequestMoreItems={() => { /* NOOP */ }}
							/>
							{items.length > 0 && (
								<AiAssistantFabSpacing />
							)}
						</>
					}
					noResultConfig={noResultConfig}
				>
					{(bookmark): ReactElement => {
						const selectable: SelectableItemOptions = selectionFor(bookmark);
						const isSelected: boolean = selectable && selectable.isSelected;
						return (
							<TableView.Row
								trackingId={`${bookmark.id}`}
								selectable={selectable}
								onContextMenu={contextMenuHandler}
								onExecute={() => onOpenItem(bookmark)}
								onIsHoverChange={(isHover: boolean) => hoverHandler(bookmark.id, isHover)}
							>
								<TableView.Cell>
									<DocumentNameWithQuickActions
										selectionActions={selectionActions}
										actionContext={{...actionContext, selection: [bookmark]}}
										isTouch={isTouch}
										isSelected={isSelected}
										isRowHovered={hoveredRowId === bookmark.id}
										name={bookmark.name}
										maxActionCount={4}
									>
										<DocumentName
											name={bookmark.name}
											documentKind={bookmark.itemType}
											mode={{
												kind: DocumentNameKind.Bookmark,
												extension: bookmark.extension
											}}
											onClick={() => onOpenItem(bookmark)}
										/>
									</DocumentNameWithQuickActions>
								</TableView.Cell>
							</TableView.Row>
						);
					}}
				</TableView>

				{contextMenuProps && (
					<ContextMenu
						{...contextMenuProps}
						actions={selectionActions}
						context={actionContext}
						trackingId="bookmark-context-menu"
						title={computeContextMenuTitle(
							actionContext.selection,
							(x) => x.name
						)}
					/>
				)}
			</>
		);
	}
);
BookmarksTableView.displayName = "BookmarksTableView";

const columnDefinitions: readonly VirtualTableViewColumnDefinition[] = [
	{
		title: localize("_BookmarkName"),
		trackingId: "name",
		width: "fill",
		maxWidth: 800
	}
];
